import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { DocumentRegisterResponse } from '@risksmart-app/trpc/src/types';
import type { GetDocumentsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC document data to match the GraphQL query structure
 */
export function mapTrpcDocumentsToGraphQL(
  trpcData: DocumentRegisterResponse | undefined
): GetDocumentsQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  const documentWithLatestPublishedVersion = trpcData?.document.map(
    (document) => {
      const previouslyPublishedVersions = document?.documentFiles.filter(
        (documentFile) => !!documentFile.PublishedDate
      );

      const latestPublishedVersion = _.orderBy(
        previouslyPublishedVersions,
        ['PublishedDate'],
        ['desc']
      )[0];

      const latestDocumentFile = _.orderBy(
        document?.documentFiles,
        ['CreatedAtTimestamp'],
        ['desc']
      )[0];

      return {
        ...document,
        documentFiles: [latestDocumentFile],
        latestPublishedVersion: [
          {
            PublishedDate: latestPublishedVersion?.PublishedDate,
          },
        ],
      };
    }
  );

  return {
    document: documentWithLatestPublishedVersion,
    assessment_result_parent: trpcData.assessment_result_parent,
  };
}

export const useGetPolicyRegister = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.document.register.queryOptions(),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcDocumentsToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
